import { useCallback, useContext, useEffect, useRef } from 'react';
import { bindAll } from 'bind-event-listener';
import { LevelContext, TopLevelContext } from '../components/layering-context';
const ESCAPE = 'Escape';
/**
 * Calls back when the escape key is pressed.
 * To be used exclusively for closing layered components.
 * Use the `isDisabled` argument to ignore closing events.
 *
 * ```js
 * useCloseOnEscapePress({
 *   onClose: () => {},
 *   isDisabled: false,
 * });
 * ```
 */
export function useCloseOnEscapePress({
  onClose,
  isDisabled
}) {
  const escapePressed = useRef(false);
  const {
    isLayerDisabled
  } = useLayering();
  const onKeyDown = useCallback(e => {
    const isDisabledLayer = isLayerDisabled();
    if (isDisabled || escapePressed.current || e.key !== ESCAPE || isDisabledLayer) {
      // We're either already handling the key down event or it's not escape or disabled.
      // Bail early!
      return;
    }
    escapePressed.current = true;
    onClose(e);
  }, [onClose, isDisabled, isLayerDisabled]);
  const onKeyUp = useCallback(() => {
    escapePressed.current = false;
  }, []);
  useEffect(() => {
    return bindAll(window, [{
      type: 'keydown',
      listener: onKeyDown
    }, {
      type: 'keyup',
      listener: onKeyUp
    }], {
      capture: false
    });
  }, [onKeyDown, onKeyUp]);
}

/**
 *
 * @deprecated Please use useLayering instead
 *
 * Layering hook to get layering info like the current level, the top level of the given component
 *
 */
export function UNSAFE_useLayering() {
  const currentLevel = useContext(LevelContext);
  const {
    topLevelRef
  } = useContext(TopLevelContext);
  const isLayerDisabled = useCallback(() => {
    return !!topLevelRef.current && currentLevel !== topLevelRef.current;
  }, [currentLevel, topLevelRef]);
  return {
    currentLevel,
    topLevelRef,
    isLayerDisabled
  };
}
export const useLayering = UNSAFE_useLayering;